/**
 * Status glyphs of the task detail framework (workstream 14, design `Language.dc.html`). 16px inline stroke SVG in
 * `currentColor`, always `aria-hidden`: a glyph never carries meaning alone and must sit next to its word. A check
 * mark only ever means verified.
 *
 * Colour follows the app-wide status scale (`StatusKind` → `Tone`, see `status.tsx`): each glyph kind has a default
 * kind (`GLYPH_STATUS_KINDS`); a caller with a real contract status passes `statusKind` (from `TASK_STATE_KINDS`,
 * `APPROVAL_STATUS_KINDS`, …) or `tone` instead. The glyph takes the solid hue (`ICON_TONES`), the word the
 * contrast-safe `-ink` hue (`TEXT_TONES`, the Badge `text` variant); neutral leaves the word in the surrounding ink.
 */
import type { ReactNode } from 'react';
import { cn, ICON_TONES, TEXT_TONES, type Tone } from './primitives.tsx';
import { STATUS_KIND_TONES, type StatusKind } from './status.tsx';

export type GlyphKind =
  | 'verified'
  | 'open'
  | 'unknown'
  | 'conflicting'
  | 'blocked'
  | 'failed'
  | 'waiting'
  | 'expired'
  /** The run ended; says nothing about the business issue. */
  | 'finished'
  /** Some business effects in place, others still open. */
  | 'partial';

/** Every glyph kind, in the order of the design's status table. */
export const GLYPH_KINDS: readonly GlyphKind[] = [
  'verified',
  'open',
  'unknown',
  'conflicting',
  'blocked',
  'failed',
  'waiting',
  'expired',
  'finished',
  'partial',
];

/**
 * Default status kind of each glyph, used when the caller has no contract status at hand. Outstanding checks, gates,
 * unknowns and blocked items are `caution`; things that simply have not happened yet are passed `ended` by the caller.
 */
export const GLYPH_STATUS_KINDS: Readonly<Record<GlyphKind, StatusKind>> = {
  verified: 'done',
  open: 'caution',
  unknown: 'caution',
  conflicting: 'problem',
  blocked: 'caution',
  failed: 'problem',
  waiting: 'action',
  expired: 'ended',
  finished: 'done',
  partial: 'caution',
};

/** Tone of a glyph: explicit `tone`, else the tone of `statusKind`, else the glyph's default kind. */
export function glyphTone({
  kind,
  tone,
  statusKind,
}: Pick<GlyphProps, 'kind' | 'tone' | 'statusKind'>): Tone {
  return tone ?? STATUS_KIND_TONES[statusKind ?? GLYPH_STATUS_KINDS[kind]];
}

const CLOCK = (
  <>
    <circle cx="8" cy="8" r="6.5" />
    <path d="M8 4.5V8l2.5 1.6" />
  </>
);

const PATHS: Record<GlyphKind, ReactNode> = {
  verified: (
    <>
      <circle cx="8" cy="8" r="6.5" />
      <path d="M5 8.2l2 2 4-4.4" />
    </>
  ),
  open: <circle cx="8" cy="8" r="6.5" />,
  unknown: (
    <>
      <circle cx="8" cy="8" r="6.5" strokeDasharray="2.4 2" />
      <path d="M6.3 6.3a1.8 1.8 0 113 1.3c-.8.5-1.3.9-1.3 1.8" />
      <path d="M8 11.6v.1" />
    </>
  ),
  conflicting: <path d="M2 5.5h10.5L10 3M14 10.5H3.5L6 13" />,
  blocked: (
    <>
      <rect x="3.5" y="7" width="9" height="7" rx="1" />
      <path d="M5.5 7V5a2.5 2.5 0 015 0v2" />
    </>
  ),
  failed: (
    <>
      <circle cx="8" cy="8" r="6.5" />
      <path d="M5.6 5.6l4.8 4.8M10.4 5.6l-4.8 4.8" />
    </>
  ),
  waiting: CLOCK,
  expired: CLOCK,
  finished: (
    <>
      <circle cx="8" cy="8" r="6.5" />
      <circle cx="8" cy="8" r="2.2" fill="currentColor" />
    </>
  ),
  partial: (
    <>
      <circle cx="8" cy="8" r="6.5" />
      <path d="M8 1.5a6.5 6.5 0 010 13z" fill="currentColor" />
    </>
  ),
};

export interface GlyphProps {
  kind: GlyphKind;
  /** Status kind from a contract map (`TASK_STATE_KINDS`, …); overrides the glyph's default kind. */
  statusKind?: StatusKind;
  /** Explicit tone; overrides `statusKind` and the default. */
  tone?: Tone;
  className?: string;
}

/** One status glyph (16px, decorative), in its status tone. Put the status word next to it. */
export function Glyph({ kind, statusKind, tone, className }: GlyphProps) {
  const t = glyphTone({ kind, statusKind, tone });
  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
      data-glyph={kind}
      data-tone={t}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('size-4 shrink-0', ICON_TONES[t], className)}
    >
      {PATHS[kind]}
    </svg>
  );
}

export interface GlyphLabelProps extends GlyphProps {
  children: ReactNode;
  /**
   * Keep the text in the surrounding ink and colour only the glyph. For sentences and claims next to a glyph, where
   * the text is not a status word.
   */
  plainText?: boolean;
}

/** A glyph with its word: the only way a status is shown in the task detail framework. */
export function GlyphLabel({ kind, statusKind, tone, children, className, plainText }: GlyphLabelProps) {
  const t = glyphTone({ kind, statusKind, tone });
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)} data-tone={t}>
      <Glyph kind={kind} tone={t} />
      <span className={plainText || t === 'neutral' ? undefined : TEXT_TONES[t]}>{children}</span>
    </span>
  );
}
